import { Box, Divider, IconButton, useTheme, alpha } from "@mui/material";
import DynamicChart from "../../../../componentes/charts";

import { JSX, useEffect, useMemo, useState } from "react";

import DynamicMetricsCards from "../../../../componentes/metricsCards";
import { Interval } from "../../../../functions/formatData/types";
import MyHeader from "../../myHeader";
import ErroMessage from "../../../../componentes/errorMessage";
import { useMediaQuery } from "@mui/system";
import Progress from "../../../../componentes/progess";
import OptionsControls from "../settingChartOptions";
import { MoreVert } from "@mui/icons-material";

interface TemplateGraphicProps {
  graphic: DashGraphicsCards;
  index: number;
  card?: DashCards;
  loading?: boolean;
  isEditing?: boolean;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  editMode?: boolean;
  updateCard?: (updatedCard: DashCardsGraphics) => void;
  deleteCard?: (id: string) => void;
  duplicateCard?: (card: DashCardsGraphics) => void;
  selectedFilter?: Filters | null;
  paginaId?: string;
  firstLoading: boolean;
  filtersInitialized: boolean;
}
const MyDivider = ({
  orientation,
  separator,
}: {
  orientation: string | undefined;
  separator: string;
}) => {
  const theme = useTheme();

  return (
    <Divider
      orientation={orientation === "horizontal" ? "vertical" : "horizontal"}
      sx={{
        ...(orientation === "horizontal"
          ? { height: "100%" }
          : { width: "100%" }),

        ...(separator === "dividerDashed" && {
          borderStyle: "dashed",
        }),
      }}
    />
  );
};

function TemplateGraphic(props: TemplateGraphicProps): JSX.Element {
  const {
    graphic,
    index,
    card,
    loading,
    isEditing,
    setAnchorEl,
    editMode,
    updateCard,
    deleteCard,
    duplicateCard,
    selectedFilter,
    firstLoading,
    filtersInitialized,
    paginaId,
  } = props;
  const [activeBreakpoint, setActiveBreakpoint] = useState<string | null>(null);

  const granularityData = JSON.parse(
    localStorage.getItem("granularity") || "{}",
  );
  const viewModeData = JSON.parse(localStorage.getItem("viewMode") || "{}");

  const repeatByData = JSON.parse(localStorage.getItem("repeatBy") || "{}");
  const hasGranularity = graphic.config.groupBy === "date" || false;
  let hasKeyOption = graphic.config.keysOptions;
  if (hasKeyOption) {
    hasKeyOption = parseOptions(hasKeyOption);
  }

  function parseOptions(graphic: DashGraphicsCards) {
    return graphic.map((item: any) => ({
      label: item.label,
      value: item.value[0],
    }));
  }
  const [intervalStateGraphic, setIntervalStateGraphic] = useState<
    Interval | undefined
  >(
    granularityData[graphic.id]
      ? granularityData[graphic.id]
      : graphic.config.interval,
  );

  const [viewModeStateGraphic, setViewModeStateGraphic] = useState<
    string | undefined
  >(
    viewModeData[graphic.id]
      ? viewModeData[graphic.id]
      : graphic?.config.viewMode,
  );
  const [repeatByStateCard, setRepeatByStateCard] = useState<string | null>(
    repeatByData[graphic.id]
      ? repeatByData[graphic.id]
      : graphic?.config.repeatBy,
  );
  const [keysOptions, setKeysOptions] = useState<DashGraphicsCards | undefined>(
    hasKeyOption ? hasKeyOption[0].value : undefined,
  );

  const breakPointOrder = ["xs", "sm", "md", "mdl", "lg", "xl"];

  function getClosestBreakpoint(
    breakpoints: Record<string, string> | string,
    current: string | null,
  ): string | undefined {
    if (!current) return;
    if (typeof breakpoints === "object") {
      const index = breakPointOrder.indexOf(current);
      for (let i = index; i >= 0; i--) {
        const key = breakPointOrder[i];
        if (breakpoints[key]) {
          return breakpoints[key];
        }
      }
    }
    return breakpoints as string;
  }

  const theme = useTheme();
  const breakpoints = {
    xs: useMediaQuery(theme.breakpoints.only("xs")),
    sm: useMediaQuery(theme.breakpoints.only("sm")),
    md: useMediaQuery(theme.breakpoints.only("md")),
    mdl: useMediaQuery(theme.breakpoints.only("mdl")),
    lg: useMediaQuery(theme.breakpoints.only("lg")),
    xl: useMediaQuery(theme.breakpoints.only("xl")),
  };
  useEffect(() => {
    for (const [key, value] of Object.entries(breakpoints)) {
      if (value) {
        setActiveBreakpoint(key);
        break;
      }
    }
  }, [breakpoints]);

  // define a quantidade de graficos que o card tem
  // e o gap entre eles
  const graphicsLength = card?.graphics?.length ?? 1;
  const itemSizeCustom = {
    width: "100%",
    height: "100%",
  };
  const itemSize = (gap: number) =>
    getClosestBreakpoint(card.orientation, activeBreakpoint) === "horizontal"
      ? {
          width: `calc((100% - ${
            gap * (graphicsLength - 1)
          }px) / ${graphicsLength})`,
          height: "100%",
        }
      : {
          height: `calc((100% - ${
            gap * (graphicsLength - 1)
          }px) / ${graphicsLength})`,
          width: "100%",
        };

  const graphicMemo = useMemo(() => {
    if (
      (graphic.type === "bar" ||
        graphic.type === "lineBar" ||
        graphic.type === "line" ||
        graphic.type === "repeat") &&
      card.split
    ) {
      granularityData[graphic.id] = intervalStateGraphic as Interval;
      localStorage.setItem("granularity", JSON.stringify(granularityData));
      viewModeData[graphic.id] = viewModeStateGraphic as Interval;
      localStorage.setItem("viewMode", JSON.stringify(viewModeData));
      repeatByData[graphic.id] = repeatByStateCard;
      localStorage.setItem("repeatBy", JSON.stringify(repeatByData));

      return {
        ...graphic,
        config: {
          ...graphic.config,
          ...(graphic.type !== "repeat"
            ? {
                interval: intervalStateGraphic
                  ? intervalStateGraphic
                  : undefined,
                viewMode: viewModeStateGraphic,
              }
            : { repeatBy: repeatByStateCard }),
        },
      };
    }
    return graphic;
  }, [
    graphic,
    intervalStateGraphic,
    viewModeStateGraphic,
    repeatByStateCard,
    loading,
  ]);

  return graphic.type !== "cards" ? (
    <>
      {editMode && !isEditing && (
        <Box
          sx={{
            position: "absolute",
            right: 6,
            top: 6,
            zIndex: 99,
          }}
        >
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setAnchorEl(e.currentTarget);
            }}
            size="small"
            className="cancelSelectorName"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Progress loading={loading} />
      <Box
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            position: "relative",
            marginTop: graphic.type === "repeat" ? 4 : 0,
          },
          card.divisionEqual == false && !graphic.error
            ? itemSizeCustom
            : itemSize(24),
        ]}
        key={index + "card"}
      >
        {card?.split && (
          <MyHeader
            title={graphic.title}
            subtitle={graphic.subtitle}
            description={graphic.description}
            noPadding={card.noPadding}
          >
            {(graphicMemo.type === "bar" ||
              graphicMemo.type === "lineBar" ||
              graphicMemo.type === "line" ||
              graphicMemo.type === "repeat") &&
              !editMode && (
                <OptionsControls
                  hasKeyOption={hasKeyOption}
                  hasGranularity={hasGranularity}
                  keysOptions={keysOptions}
                  setKeysOptions={setKeysOptions}
                  intervalStateCard={intervalStateGraphic}
                  setIntervalStateCard={(value: string) =>
                    setIntervalStateGraphic(value as Interval)
                  }
                  viewModeStateCard={viewModeStateGraphic}
                  setViewModeStateCard={(value: string) =>
                    setViewModeStateGraphic(value as string)
                  }
                  repeatByStateCard={repeatByStateCard}
                  setRepeatByStateCard={setRepeatByStateCard}
                />
              )}
          </MyHeader>
        )}

        {graphic.error ? (
          <ErroMessage error={graphic.error} />
        ) : (
          <DynamicChart
            key={`${index} + graph`}
            updateCard={updateCard}
            deleteCard={deleteCard}
            duplicateCard={duplicateCard}
            idCard={card.id}
            selectedFilter={selectedFilter}
            paginaId={paginaId}
            firstLoading={firstLoading}
            filtersInitialized={filtersInitialized}
            loading={loading}
            {...(card?.split ? graphicMemo : graphic)}
          />
        )}
        {card?.separator && index < card.graphics?.length - 1 && (
          <MyDivider
            orientation={getClosestBreakpoint(
              card.orientation,
              activeBreakpoint,
            )}
            separator={card.separator}
          />
        )}
      </Box>
    </>
  ) : (
    <>
      <Box
        sx={{
          ...(card.divisionEqual == false ? itemSizeCustom : itemSize(24)),
          position: "relative",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <Progress loading={loading} />

        {graphic.error ? (
          <ErroMessage {...graphic} />
        ) : (
          <DynamicMetricsCards
            {...graphic}
            key={`${index} ${loading} + card`}
          />
        )}

        {editMode && !isEditing && (
          <Box
            sx={{
              position: "absolute",
              width: "32px",
              height: "44px",
              right: 0,
              top: 0,
              zIndex: 99,
              background: alpha(theme.palette.background.paper, 0.3),
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
              size="small"
              className="cancelSelectorName"
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </>
  );
}
export default TemplateGraphic;
